import { useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
// import { Siren } from "lucide-react"; // ← accès S.O.S. retiré (cf. plus bas)
import { ClientAside } from "@/features/client/components/aside/ClientAside";
import { HomeTopBar } from "@/features/client/components/home/HomeTopBar";
import { LocationGate } from "./LocationGate";
import { LocationPermissionDialog } from "./LocationPermissionDialog";
import { CLIENT_NAV } from "./navItems";
import { ApplicationLayout } from "./ApplicationLayout";
import { useIsTabletUp } from "@/shared/hooks/useMediaQuery";
import { useDeviceRegistration } from "@/features/notifications/useDeviceRegistration";
import { useRealtimeResync } from "@/shared/realtime";
import { usePreferencesStore } from "@/shared/stores/preferencesStore";
import { useSessionStore } from "@/shared/stores/sessionStore";
import { useSupportChat } from "@/features/support";
// `FloatingSOSButton` retiré de cet import — accès S.O.S. désactivé (cf. plus bas).
import { paths } from "../routes/paths";

// Shell applicatif responsive — implémente §6 de MIGRATION_WEB.md.
//
//   < md   : contenu plein écran + tab bar en bas  (design natif porté)
//   ≥ md   : la tab bar devient une sidebar persistante à gauche
//   ≥ lg   : contenu contraint à max-w-content, centré dans l'espace restant
//   ≥ xl   : colonne contextuelle à droite (cf. ClientAside)
//
// Le body ne scrolle pas : c'est <main> qui porte le scroll, pour que sidebar et
// tab bar restent fixes sans `position: fixed` ni compensation de hauteur.

// ─── Accès S.O.S. — RETIRÉ DE L'INTERFACE (demande produit) ──────────────────
//
// Les deux points d'entrée vers l'urgence sont désactivés sur TOUS les formats :
// l'entrée « Urgence » de la sidebar (≥ md) et le `FloatingSOSButton` en bas à
// droite (mobile). Conservés en commentaire à la demande, pour pouvoir être
// rétablis tels quels.
//
// ⚠️ Les écrans et les routes `/sos*` existent toujours et restent accessibles
// par URL directe : seuls les boutons disparaissent. Si l'intention est de
// fermer la fonctionnalité, il faut aussi retirer les routes du router.
//
// /**
//  * Entrée « Urgence » de la sidebar (≥ md).
//  *
//  * En mobile, c'est le `FloatingSOSButton` du design system qui prend le relais :
//  * il est posé en bas à droite, à portée de pouce, et porte le halo pulsant de
//  * la marque. Sur desktop ce placement n'a plus de sens (loin du regard, aucune
//  * notion de pouce), d'où cette entrée de navigation classique.
//  */
// function SosNavLink() {
//   return (
//     <NavLink
//       to={paths.emergency()}
//       aria-label="Urgence SOS"
//       className="flex items-center gap-3 rounded-btn bg-sos-600 px-4 py-3 font-bold text-white transition-colors hover:bg-sos-400"
//     >
//       <Siren size={20} aria-hidden />
//       <span>Urgence</span>
//     </NavLink>
//   );
// }

export function AppShell() {
  // Reprise du temps réel : quand le socket revient SANS rejeu de session,
  // les événements de la coupure sont perdus. On recharge alors les données
  // plutôt que de laisser un écran figé sur un état périmé. Monté ici, une
  // seule fois pour tout l'espace client.
  // Ré-enregistre l'appareil push à chaque démarrage et reprise réseau
  // (idempotent côté serveur, et sans jamais demander la permission).
  useDeviceRegistration();

  useRealtimeResync();

  const isTabletUp = useIsTabletUp();
  const navigate = useNavigate();
  const supportChat = useSupportChat();

  // Identité affichée dans la barre supérieure. Prénom = 1er mot du claim
  // « name » du JWT ; sans nom, `HomeTopBar` n'affiche que le logo — on
  // n'invente pas de salutation.
  const displayName = useSessionStore((s) => s.displayName);
  const firstName = displayName?.trim().split(/\s+/)[0];

  // Toute 1ʳᵉ visite → « Bienvenue » au lieu de « Content de vous revoir ».
  // Le flag est LU une fois et figé dans une ref : l'accueil le marque comme vu
  // dans un effet de montage, et sans ce gel la salutation basculerait sous les
  // yeux de l'utilisateur au premier rendu.
  const hasVisitedHome = usePreferencesStore((s) => s.hasVisitedHome);
  const isFirstVisitRef = useRef(!hasVisitedHome);

  return (
    <>
      <LocationGate />
      <LocationPermissionDialog />
      <ApplicationLayout
        navigation={CLIENT_NAV}
        aside={<ClientAside />}
        header={
          isTabletUp ? (
            <header className="flex shrink-0 items-center border-b border-gray-100 bg-white px-8">
              <div className="min-w-0 flex-1">
                <HomeTopBar
                  firstName={firstName}
                  isFirstVisit={isFirstVisitRef.current}
                  onSupportPress={() => void supportChat.openChat()}
                  isSupportOpening={supportChat.isOpening}
                  onNotificationsPress={() => navigate(paths.notifications())}
                  showLogo={false}
                />
              </div>
            </header>
          ) : undefined
        }
      >
        <Outlet />
      </ApplicationLayout>
    </>
  );
}
